import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  FaTag, FaPlus, FaCheck, FaTimes, FaBuilding,
  FaExclamationTriangle, FaSearch, FaBroom,
} from 'react-icons/fa';
import { MdWarning, MdAccessTime, MdAutorenew } from 'react-icons/md';
import saApi from '../../services/superAdminApi';
import { getPlans } from '../../services/subscriptionService';
import useEscapeKey from '../../hooks/useEscapeKey';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const TYPE_LABEL = {
  percentage:       'Percentage',
  fixed_amount:     'Fixed Amount',
  free_months:      'Free Months',
  trial_extension:  'Trial Extension',
};

const DURATION_LABEL = {
  once:       'Once',
  repeating:  'Repeating',
  forever:    'Forever',
};

const TYPE_BADGE_COLOR = {
  percentage:      { bg: 'rgba(96,165,250,0.12)',  color: '#60a5fa' },
  fixed_amount:    { bg: 'rgba(52,211,153,0.12)',  color: '#34d399' },
  free_months:     { bg: 'rgba(192,132,252,0.12)', color: '#c084fc' },
  trial_extension: { bg: 'rgba(252,211,77,0.12)',  color: '#fcd34d' },
};

function Pill({ label, bg, color }) {
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
      style={{ background: bg, color }}>
      {label}
    </span>
  );
}

// ── Confirm modal (replaces window.confirm) — matches the ClubDetail pattern ───
function ConfirmModal({ title, message, confirmLabel, tone = 'danger', onCancel, onConfirm }) {
  const btnBg = tone === 'success' ? '#059669' : tone === 'warn' ? '#d97706' : '#c8102e';
  useEscapeKey(onCancel);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4"
        style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-[#ede8e0]">{title}</h2>
        <p className="text-sm leading-relaxed" style={{ color: '#c5bfb5' }}>{message}</p>
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onCancel} className="text-sm font-semibold px-4 py-2 rounded-lg" style={{ background: '#28261f', color: '#ede8e0' }}>Cancel</button>
          <button onClick={onConfirm} className="text-sm font-semibold px-4 py-2 rounded-lg text-[#ede8e0]" style={{ background: btnBg }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Warning Banner ────────────────────────────────────────────────────────────
function WarningBanner({ staleApplied, expiringApplied, foreverApplied, highDiscounts, onCleanup, cleaning }) {
  const items = [
    staleApplied.length  > 0 && { icon: <FaBroom className="text-red-400" />,    color: '#ef4444', label: `${staleApplied.length} stale`,       desc: 'Active but past expiry — revenue leakage risk', action: true },
    expiringApplied.length > 0 && { icon: <MdAccessTime className="text-yellow-400" />, color: '#f59e0b', label: `${expiringApplied.length} expiring ≤7d`, desc: 'Discounts ending soon — check with club', action: false },
    foreverApplied.length  > 0 && { icon: <MdAutorenew className="text-purple-400" />,  color: '#a855f7', label: `${foreverApplied.length} forever`,     desc: 'No end date — review periodically', action: false },
    highDiscounts.length   > 0 && { icon: <MdWarning className="text-orange-400" />,    color: '#f97316', label: `${highDiscounts.length} high (>50%)`,  desc: 'Large discounts applied', action: false },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div className="rounded-xl p-4 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#6b6560' }}>
          Discount Warnings
        </p>
        {staleApplied.length > 0 && (
          <button
            onClick={onCleanup}
            disabled={cleaning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#ede8e0] transition-colors"
            style={{ background: cleaning ? '#28261f' : '#c8102e', border: '1px solid #e01535' }}>
            <FaBroom className="text-xs" />
            {cleaning ? 'Cleaning…' : 'Clean Up Stale'}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg px-3 py-2.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item.color}33` }}>
            <div className="mt-0.5 flex-shrink-0">{item.icon}</div>
            <div>
              <p className="text-sm font-bold" style={{ color: item.color }}>{item.label}</p>
              <p className="text-xs mt-0.5" style={{ color: '#6b6560' }}>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Create Discount Modal ─────────────────────────────────────────────────────
function CreateDiscountModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', code: '', type: 'percentage', value: '', plan: 'pro',
    duration: 'once', durationMonths: '', reason: '', internalNote: '',
    startsAt: '', expiresAt: '', maxRedemptions: '',
  });
  const [submitting, setSubmitting] = useState(false);
  useEscapeKey(onClose, !submitting);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        code: form.code,
        type: form.type,
        value: Number(form.value),
        plan: form.plan,
        duration: form.duration,
        reason: form.reason,
        internalNote: form.internalNote,
      };
      if (form.duration === 'repeating') payload.durationMonths = Number(form.durationMonths);
      if (form.startsAt) payload.startsAt = form.startsAt;
      if (form.expiresAt) payload.expiresAt = form.expiresAt;
      if (form.maxRedemptions) payload.maxRedemptions = Number(form.maxRedemptions);

      const res = await saApi.post('/api/super-admin/discounts', payload);
      toast.success('Discount created');
      onCreated(res.data.discount);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create discount');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)',
    borderRadius: 10, padding: '8px 12px', color: '#ede8e0', fontSize: 13, outline: 'none',
    colorScheme: 'dark', // native <select> dropdowns render dark, not white
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8a847c', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-lg rounded-2xl flex flex-col"
        style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
          <h2 className="font-bold text-[#ede8e0] flex items-center gap-2"><FaTag className="text-red-400" /> New Discount</h2>
          <button onClick={onClose} className="text-[#8a847c] hover:text-[#ede8e0]"><FaTimes /></button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. Nepal Opener 2025" required />
            </div>
            <div>
              <label style={labelStyle}>Code (internal)</label>
              <input style={inputStyle} value={form.code} onChange={e => set('code', e.target.value.toUpperCase())}
                placeholder="e.g. OPENER25" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed_amount">Fixed Amount (NPR)</option>
                <option value="free_months">Free Months</option>
                <option value="trial_extension">Trial Extension (days)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                {form.type === 'percentage' ? 'Percentage (%)' :
                 form.type === 'fixed_amount' ? 'Amount (NPR)' :
                 form.type === 'free_months' ? 'Number of Months' : 'Number of Days'}
              </label>
              <input style={inputStyle} type="number" min="0" value={form.value}
                onChange={e => set('value', e.target.value)} required
                max={form.type === 'percentage' ? 100 : undefined} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Plan</label>
              <select style={inputStyle} value={form.plan} onChange={e => set('plan', e.target.value)}>
                <option value="pro">Pro</option>
                <option value="free">Free</option>
                <option value="all">All Plans</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Duration</label>
              <select style={inputStyle} value={form.duration} onChange={e => set('duration', e.target.value)}>
                <option value="once">Once (applies to one billing cycle)</option>
                <option value="repeating">Repeating (N months)</option>
                <option value="forever">Forever</option>
              </select>
            </div>
          </div>

          {form.duration === 'forever' && (
            <div className="flex items-start gap-2 rounded-xl px-4 py-3"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <FaExclamationTriangle className="text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm" style={{ color: '#fca5a5' }}>
                <strong>Forever discount warning:</strong> This permanently reduces monthly revenue for the selected club with no automatic end date. Use only for long-term partnership agreements.
              </p>
            </div>
          )}

          {form.duration === 'repeating' && (
            <div>
              <label style={labelStyle}>Duration (months)</label>
              <input style={inputStyle} type="number" min="1" value={form.durationMonths}
                onChange={e => set('durationMonths', e.target.value)} required placeholder="e.g. 3" />
            </div>
          )}

          <div>
            <label style={labelStyle}>Reason (internal, why is this discount being given)</label>
            <input style={inputStyle} value={form.reason} onChange={e => set('reason', e.target.value)}
              placeholder="e.g. Early adopter partner club, signed MOU in Jan 2025" />
          </div>

          <div>
            <label style={labelStyle}>Internal Note</label>
            <textarea style={{ ...inputStyle, resize: 'none' }} rows={2} value={form.internalNote}
              onChange={e => set('internalNote', e.target.value)}
              placeholder="Any additional notes for your team..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Starts At (optional)</label>
              <input style={inputStyle} type="date" value={form.startsAt} onChange={e => set('startsAt', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Expires At (optional)</label>
              <input style={inputStyle} type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Max Redemptions (optional)</label>
            <input style={inputStyle} type="number" min="1" value={form.maxRedemptions}
              onChange={e => set('maxRedemptions', e.target.value)}
              placeholder="Leave blank for unlimited" />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#8a847c] hover:text-[#ede8e0] border transition-colors"
              style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors"
              style={{ background: submitting ? '#28261f' : '#c8102e' }}>
              {submitting ? 'Creating...' : 'Create Discount'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Pricing comes from backend/config/plans.js (fetched via getPlans()), never hardcoded here —
// planPrices is { free: 0, pro: <live price> }, passed in by the caller.
function computePreview(discount, planKey, planPrices) {
  const base = planPrices[planKey] ?? planPrices.pro;
  let saving = 0;
  if (discount.type === 'percentage') {
    saving = Math.round((base * discount.value) / 100);
  } else if (discount.type === 'fixed_amount') {
    saving = Math.min(discount.value, base);
  } else if (discount.type === 'free_months' || discount.type === 'trial_extension') {
    saving = base;
  }
  return { base, saving, final: Math.max(0, base - saving) };
}

// ── Apply to Club Modal ───────────────────────────────────────────────────────
function ApplyToClubModal({ discount, onClose, onApplied }) {
  const [clubSearch, setClubSearch] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [reason, setReason] = useState(discount.reason || '');
  const [submitting, setSubmitting] = useState(false);
  useEscapeKey(onClose, !submitting);

  const { data: clubsData } = useQuery({
    queryKey: ['sa-clubs-search', clubSearch],
    queryFn: () => saApi.get('/api/super-admin/clubs', { params: { search: clubSearch, limit: 10 } }).then(r => r.data),
    enabled: clubSearch.length > 0,
  });
  const clubs = clubsData?.clubs || [];

  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const planPrices = { free: 0, pro: plansData?.pro?.price ?? 0 };

  const handleApply = async () => {
    if (!selectedClub) return toast.error('Select a club first');
    setSubmitting(true);
    try {
      await saApi.post(`/api/super-admin/discounts/${discount._id}/apply-to-club`, {
        clubId: selectedClub._id,
        reason,
      });
      toast.success(`Discount applied to ${selectedClub.name}`);
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to apply discount');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)',
    borderRadius: 10, padding: '8px 12px', color: '#ede8e0', fontSize: 13, outline: 'none',
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-md rounded-2xl" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
          <h2 className="font-bold text-[#ede8e0]">Apply Discount to Club</h2>
          <button onClick={onClose} className="text-[#8a847c] hover:text-[#ede8e0]"><FaTimes /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl px-4 py-3" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
            <p className="text-xs font-semibold text-[#8a847c] mb-0.5">Discount</p>
            <p className="text-sm font-bold text-[#ede8e0]">{discount.name}</p>
            <p className="text-xs text-[#8a847c] mt-0.5">
              {TYPE_LABEL[discount.type]} — {discount.type === 'percentage' ? `${discount.value}%` : `NPR ${Number(discount.value).toLocaleString()}`} |{' '}
              {DURATION_LABEL[discount.duration]}{discount.durationMonths ? ` (${discount.durationMonths} months)` : ''}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#8a847c] mb-1.5 uppercase tracking-wide">Search Club</label>
            <input style={inputStyle} value={clubSearch}
              onChange={e => { setClubSearch(e.target.value); setSelectedClub(null); }}
              placeholder="Type club name..." />
            {clubs.length > 0 && !selectedClub && (
              <div className="mt-1 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(237,232,224,0.08)' }}>
                {clubs.map(c => (
                  <button key={c._id} onClick={() => { setSelectedClub(c); setClubSearch(c.name); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#28261f] transition-colors">
                    <FaBuilding className="text-[#6b6560] flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-[#ede8e0]">{c.name}</p>
                      <p className="text-xs text-[#8a847c]">{c.subscriptionPlan} plan · {c.status}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedClub && (() => {
            const plan    = selectedClub.subscriptionPlan || 'pro';
            const preview = computePreview(discount, plan, planPrices);
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-2 rounded-xl px-4 py-2.5" style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.2)' }}>
                  <FaCheck className="text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-300">{selectedClub.name}</p>
                    <p className="text-xs text-green-600 capitalize">{plan} plan</p>
                  </div>
                </div>
                {preview.base > 0 && (
                  <div className="rounded-xl px-4 py-3 space-y-1.5" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
                    <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b6560' }}>Price Preview</p>
                    <div className="flex justify-between text-sm">
                      <span style={{ color: '#8a847c' }}>Original</span>
                      <span style={{ color: '#c5bfb5' }}>NPR {preview.base.toLocaleString()}/mo</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span style={{ color: '#8a847c' }}>Discount</span>
                      <span style={{ color: '#34d399' }}>-NPR {preview.saving.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold pt-1 border-t" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                      <span style={{ color: '#c5bfb5' }}>Final</span>
                      <span style={{ color: '#34d399' }}>NPR {preview.final.toLocaleString()}/mo</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <div>
            <label className="block text-xs font-semibold text-[#8a847c] mb-1.5 uppercase tracking-wide">Reason (optional override)</label>
            <input style={inputStyle} value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Why is this club receiving this discount?" />
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#8a847c] hover:text-[#ede8e0] border transition-colors"
              style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
              Cancel
            </button>
            <button onClick={handleApply} disabled={submitting || !selectedClub}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors"
              style={{ background: submitting || !selectedClub ? '#28261f' : '#c8102e' }}>
              {submitting ? 'Applying...' : 'Apply Discount'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Discounts() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('discounts'); // 'discounts' | 'applied'
  const [showCreate, setShowCreate] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, tone, onConfirm }

  const { data: discountsData, isPending: loadingDiscounts } = useQuery({
    queryKey: ['sa-discounts'],
    queryFn: () => saApi.get('/api/super-admin/discounts').then(r => r.data),
  });
  const discounts = discountsData?.discounts || [];

  // Always fetch applied discounts (needed for warning panel, not just the applied tab)
  const { data: appliedData, isPending: loadingApplied } = useQuery({
    queryKey: ['sa-applied-discounts'],
    queryFn: () => saApi.get('/api/super-admin/discounts/applied').then(r => r.data),
  });
  const applied = appliedData?.appliedDiscounts || [];

  // Warning computations
  const now = new Date();
  const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const staleApplied    = applied.filter(a => a.isActive && a.expiresAt && new Date(a.expiresAt) < now);
  const expiringApplied = applied.filter(a => a.isActive && a.expiresAt && new Date(a.expiresAt) >= now && new Date(a.expiresAt) <= sevenDays);
  const foreverApplied  = applied.filter(a => a.isActive && a.duration === 'forever');
  const highDiscounts   = discounts.filter(d => d.isActive && d.type === 'percentage' && d.value > 50);

  const cleanupMutation = useMutation({
    mutationFn: () => saApi.post('/api/super-admin/discounts/cleanup-expired'),
    onSuccess: (res) => {
      toast.success(`Cleaned up ${res.data.cleaned ?? 0} stale discount(s)`);
      qc.invalidateQueries({ queryKey: ['sa-applied-discounts'] });
      qc.invalidateQueries({ queryKey: ['sa-discounts'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Cleanup failed'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id) => saApi.post(`/api/super-admin/discounts/${id}/deactivate`),
    onSuccess: () => { toast.success('Discount deactivated'); qc.invalidateQueries({ queryKey: ['sa-discounts'] }); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to deactivate'),
  });

  const removeMutation = useMutation({
    mutationFn: (clubId) => saApi.post('/api/super-admin/discounts/remove-from-club', { clubId }),
    onSuccess: () => { toast.success('Discount removed from club'); qc.invalidateQueries({ queryKey: ['sa-applied-discounts'] }); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to remove'),
  });

  const cell = { fontSize: 13, padding: '14px 16px', borderBottom: '1px solid rgba(237,232,224,0.05)', color: '#c5bfb5', verticalAlign: 'top' };
  const th   = { fontSize: 11, fontWeight: 700, color: '#6b6560', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', background: '#1a1918' };

  // Client-side search filter
  const q = search.toLowerCase();
  const filteredDiscounts = discounts.filter(d =>
    !q || d.name?.toLowerCase().includes(q) || d.code?.toLowerCase().includes(q) || d.reason?.toLowerCase().includes(q)
  );
  const filteredApplied = applied.filter(a =>
    !q || a.clubId?.name?.toLowerCase().includes(q) || a.discountId?.name?.toLowerCase().includes(q) || a.discountId?.code?.toLowerCase().includes(q)
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#ede8e0] flex items-center gap-2"><FaTag className="text-red-400" /> Discounts</h1>
          <p className="text-sm mt-0.5" style={{ color: '#6b6560' }}>
            Relationship-based discounts applied manually to specific clubs. Not public coupons.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors"
          style={{ background: '#c8102e' }}>
          <FaPlus /> New Discount
        </button>
      </div>

      {/* Warnings panel */}
      <WarningBanner
        staleApplied={staleApplied}
        expiringApplied={expiringApplied}
        foreverApplied={foreverApplied}
        highDiscounts={highDiscounts}
        onCleanup={() => setConfirmState({
          title: 'Clean up stale discounts?',
          message: `Mark ${staleApplied.length} expired discount(s) as inactive? This stops them being counted in revenue calculations. This cannot be undone.`,
          confirmLabel: 'Clean Up',
          tone: 'danger',
          onConfirm: () => { cleanupMutation.mutate(); setConfirmState(null); },
        })}
        cleaning={cleanupMutation.isPending}
      />

      {/* Filter row — tabs left, search right */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 p-1 rounded-xl h-10 items-center" style={{ background: '#1e1d1b' }}>
          {[['discounts', `Templates (${discounts.length})`], ['applied', `Applied (${applied.filter(a => a.isActive).length})`]].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setSearch(''); }}
              className="px-4 h-8 rounded-lg text-sm font-medium transition-colors"
              style={tab === key ? { background: '#c8102e', color: '#fff' } : { color: '#8a847c' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 px-3 h-10 rounded-xl w-full sm:w-64"
          style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }}>
          <FaSearch className="text-[#6b6560] flex-shrink-0 text-xs" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, code..."
            className="bg-transparent text-sm outline-none flex-1"
            style={{ color: '#ede8e0' }}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-[#6b6560] hover:text-[#ede8e0]">
              <FaTimes className="text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* ── Discount Templates ── */}
      {tab === 'discounts' && (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }}>
          {loadingDiscounts ? (
            <p className="p-8 text-center text-sm text-[#6b6560]">Loading...</p>
          ) : filteredDiscounts.length === 0 ? (
            <div className="py-10 px-6 flex flex-col items-center text-center">
              <FaTag className="text-2xl mb-2.5" style={{ color: '#6b6560' }} />
              <p className="text-sm font-medium text-[#8a847c]">
                {search ? 'No discounts match your search' : 'No discounts yet'}
              </p>
              {!search && (
                <>
                  <p className="text-xs text-[#6b6560] mt-1 mb-4">Create one to apply to clubs with special pricing agreements.</p>
                  <button onClick={() => setShowCreate(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors"
                    style={{ background: '#c8102e' }}>
                    <FaPlus /> New Discount
                  </button>
                </>
              )}
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Name / Code', 'Type', 'Value', 'Plan', 'Duration', 'Redeemed', 'Status', 'Actions'].map(h => (
                    <th key={h} style={th} className="text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDiscounts.map(d => {
                  const tc = TYPE_BADGE_COLOR[d.type] || { bg: 'rgba(107,101,96,0.2)', color: '#8a847c' };
                  const isHighDiscount = d.isActive && d.type === 'percentage' && d.value > 50;
                  return (
                    <tr key={d._id} className="hover:bg-[#28261f] transition-colors" style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                      <td style={cell}>
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-[#ede8e0]">{d.name}</p>
                          {isHighDiscount && (
                            <FaExclamationTriangle className="text-orange-400 text-xs flex-shrink-0" title="High discount >50%" />
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: '#6b6560', fontFamily: 'monospace' }}>{d.code}</p>
                        {d.reason && <p className="text-xs mt-1" style={{ color: '#8a847c' }}>{d.reason}</p>}
                      </td>
                      <td style={cell}><Pill label={TYPE_LABEL[d.type]} bg={tc.bg} color={tc.color} /></td>
                      <td style={cell}>
                        <span className="font-semibold text-[#ede8e0]">
                          {d.type === 'percentage' ? `${d.value}%` :
                           d.type === 'fixed_amount' ? `NPR ${Number(d.value).toLocaleString()}` :
                           `${d.value} ${d.type === 'free_months' ? 'mo' : 'days'}`}
                        </span>
                      </td>
                      <td style={cell}><span className="capitalize text-[#c5bfb5]">{d.plan}</span></td>
                      <td style={cell}>
                        <span className="text-[#c5bfb5]">{DURATION_LABEL[d.duration]}</span>
                        {d.duration === 'repeating' && d.durationMonths && (
                          <span className="text-[#6b6560]"> ({d.durationMonths} mo)</span>
                        )}
                        {d.duration === 'forever' && (
                          <FaExclamationTriangle className="inline ml-1 text-yellow-400" title="Forever discount" />
                        )}
                      </td>
                      <td style={cell}>
                        <span className="text-[#c5bfb5]">{d.redeemedCount || 0}</span>
                        {d.maxRedemptions && <span className="text-[#6b6560]"> / {d.maxRedemptions}</span>}
                      </td>
                      <td style={cell}>
                        {d.isActive
                          ? <Pill label="Active"   bg="rgba(52,211,153,0.12)" color="#34d399" />
                          : <Pill label="Inactive" bg="rgba(107,101,96,0.2)" color="#8a847c" />}
                      </td>
                      <td style={cell}>
                        <div className="flex gap-2 flex-wrap">
                          {d.isActive && (
                            <>
                              <button onClick={() => setApplyTarget(d)}
                                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#ede8e0] transition-colors"
                                style={{ background: '#c8102e' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#e01535')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#c8102e')}>
                                Apply to Club
                              </button>
                              <button onClick={() => setConfirmState({
                                title: 'Deactivate discount?',
                                message: `Deactivate "${d.name}"? It will no longer be applicable.`,
                                confirmLabel: 'Deactivate',
                                tone: 'danger',
                                onConfirm: () => { deactivateMutation.mutate(d._id); setConfirmState(null); },
                              })}
                                className="px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
                                style={{ background: 'transparent', color: '#ff453a', border: '1px solid rgba(255,69,58,0.2)' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,69,58,0.08)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                Deactivate
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Applied Discounts ── */}
      {tab === 'applied' && (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }}>
          {loadingApplied ? (
            <p className="p-8 text-center text-sm text-[#6b6560]">Loading...</p>
          ) : filteredApplied.length === 0 ? (
            <div className="py-10 px-6 flex flex-col items-center text-center">
              <FaBuilding className="text-2xl mb-2.5" style={{ color: '#6b6560' }} />
              <p className="text-sm font-medium text-[#8a847c]">
                {search ? 'No applied discounts match your search' : 'No discounts applied to any club yet'}
              </p>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Club', 'Discount', 'Original', 'Saving', 'Final Price', 'Duration', 'Applied', 'Status', 'Actions'].map(h => (
                    <th key={h} style={th} className="text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredApplied.map(a => {
                  const isStale = a.isActive && a.expiresAt && new Date(a.expiresAt) < now;
                  const isExpiringSoon = a.isActive && a.expiresAt && new Date(a.expiresAt) >= now && new Date(a.expiresAt) <= sevenDays;
                  return (
                    <tr key={a._id} className="hover:bg-[#28261f] transition-colors" style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                      <td style={cell}>
                        <p className="font-semibold text-[#ede8e0]">{a.clubId?.name || a.clubId}</p>
                      </td>
                      <td style={cell}>
                        <p className="font-medium text-[#ede8e0]">{a.discountId?.name || '—'}</p>
                        {a.discountId?.code && (
                          <p className="text-xs mt-0.5" style={{ color: '#6b6560', fontFamily: 'monospace' }}>{a.discountId.code}</p>
                        )}
                      </td>
                      <td style={cell}><span className="text-[#c5bfb5]">NPR {Number(a.originalMonthlyPrice).toLocaleString()}</span></td>
                      <td style={cell}><span style={{ color: '#34d399' }}>-NPR {Number(a.discountAmount).toLocaleString()}</span></td>
                      <td style={cell}><span className="font-bold text-[#ede8e0]">NPR {Number(a.finalMonthlyPrice).toLocaleString()}</span></td>
                      <td style={cell}>
                        <span className="text-[#c5bfb5] capitalize">{a.duration}</span>
                        {a.expiresAt && (
                          <p className="text-xs mt-0.5" style={{ color: isStale ? '#ff453a' : isExpiringSoon ? '#fbbf24' : '#6b6560' }}>
                            {isStale ? '⚠ Expired ' : isExpiringSoon ? '⏰ Ends ' : 'Until '}
                            {fmtDate(a.expiresAt)}
                          </p>
                        )}
                        {a.duration === 'forever' && (
                          <FaExclamationTriangle className="inline ml-1 text-yellow-400 text-xs" title="Forever discount" />
                        )}
                      </td>
                      <td style={cell}>
                        <p className="text-[#c5bfb5]">{fmtDate(a.appliedAt)}</p>
                        {a.reason && <p className="text-xs mt-0.5 text-[#6b6560]">{a.reason}</p>}
                      </td>
                      <td style={cell}>
                        {a.isActive && !isStale
                          ? <Pill label="Active"  bg="rgba(52,211,153,0.12)" color="#34d399" />
                          : a.isActive && isStale
                          ? <Pill label="Stale"   bg="rgba(255,69,58,0.12)" color="#ff453a" />
                          : <Pill label="Removed" bg="rgba(107,101,96,0.2)" color="#8a847c" />}
                      </td>
                      <td style={cell}>
                        {a.isActive && (
                          <button onClick={() => setConfirmState({
                            title: 'Remove discount?',
                            message: `Remove discount from ${a.clubId?.name || 'this club'}? The club will return to standard pricing.`,
                            confirmLabel: 'Remove',
                            tone: 'danger',
                            onConfirm: () => { removeMutation.mutate(a.clubId?._id || a.clubId); setConfirmState(null); },
                          })}
                            className="px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
                            style={{ background: 'transparent', color: '#ff453a', border: '1px solid rgba(255,69,58,0.2)' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,69,58,0.08)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showCreate && (
        <CreateDiscountModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['sa-discounts'] }); }}
        />
      )}

      {applyTarget && (
        <ApplyToClubModal
          discount={applyTarget}
          onClose={() => setApplyTarget(null)}
          onApplied={() => { setApplyTarget(null); qc.invalidateQueries({ queryKey: ['sa-applied-discounts'] }); }}
        />
      )}

      {confirmState && (
        <ConfirmModal {...confirmState} onCancel={() => setConfirmState(null)} />
      )}
    </div>
  );
}
